import React, { useCallback, useEffect, useRef, useState } from "react"
import useModal from "./use-modal"

const Chevron = ({ direction }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d={direction === "left" ? "M15 5l-7 7 7 7" : "M9 5l7 7-7 7"} />
  </svg>
)

const Lightbox = ({ images, onClose }) => {
  const [index, setIndex] = useState(0)
  const container = useRef(null)
  const closeButton = useRef(null)
  const touchStartX = useRef(null)
  const count = images.length
  const image = images[index]

  const prev = useCallback(() => setIndex(i => (i - 1 + count) % count), [
    count,
  ])
  const next = useCallback(() => setIndex(i => (i + 1) % count), [count])

  useModal(container, closeButton, onClose)

  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === "ArrowLeft") prev()
      else if (e.key === "ArrowRight") next()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [prev, next])

  // Preload the neighbours so paging feels instant
  useEffect(() => {
    ;[index - 1, index + 1].forEach(i => {
      new Image().src = images[(i + count) % count].src
    })
  }, [index, images, count])

  const handleBackdrop = e => {
    if (e.target === e.currentTarget) onClose()
  }

  const handleTouchStart = e => {
    touchStartX.current = e.touches[0].clientX
  }

  const handleTouchEnd = e => {
    if (touchStartX.current === null) return
    const dx = e.changedTouches[0].clientX - touchStartX.current
    touchStartX.current = null
    if (dx > 50) prev()
    else if (dx < -50) next()
  }

  return (
    <div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Screenshots"
      ref={container}
      onMouseDown={handleBackdrop}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <button
        type="button"
        className="lightbox-button lightbox-close"
        aria-label="Close"
        onClick={onClose}
        ref={closeButton}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>

      <button
        type="button"
        className="lightbox-button lightbox-prev"
        aria-label="Previous screenshot"
        onClick={prev}
      >
        <Chevron direction="left" />
      </button>

      <figure className="lightbox-figure" onMouseDown={handleBackdrop}>
        <img
          key={image.src}
          className="lightbox-image"
          src={image.src}
          alt={image.caption}
          width="2000"
          height="1250"
        />
        {/* Screen readers get the caption from the image alt and the
            announcement below, so the visible caption is hidden from them */}
        <figcaption className="lightbox-caption" aria-hidden="true">
          {image.caption}
          <span className="lightbox-count">
            {index + 1} / {count}
          </span>
        </figcaption>
        <p className="visually-hidden" aria-live="polite">
          Screenshot {index + 1} of {count}: {image.caption}
        </p>
      </figure>

      <button
        type="button"
        className="lightbox-button lightbox-next"
        aria-label="Next screenshot"
        onClick={next}
      >
        <Chevron direction="right" />
      </button>
    </div>
  )
}

export default Lightbox
